/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import _get from 'lodash/get';
import _last from 'lodash/last';
import _sortBy from 'lodash/sortBy';

import { Badge } from '@mui/material';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import { useOrderResource } from '../../hooks/useOrderResource';
import Overlay from '../../components/Overlay';
import SortableItem from '../../components/SortableItem';

const CollectionGrid = ({ classes, basePath, data, ids, ...rest }) => {
  const { order, dragEnd, progress } = useOrderResource();
  const notify = useNotify();
  const [items, setItems] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    let collections = Object.values(data);
    collections = _sortBy(collections, ['weight']);
    setItems(collections);
  }, [data]);

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const { sortedItems, oldIndex, newIndex } = dragEnd(items, active, over);

      setItems(sortedItems);

      const params = {
        filter: rest.filterValues,
        pagination: { page: rest.page, perPage: rest.perPage },
        sort: rest.currentSort,
      };

      // Change the weight and refresh the list automatically
      await order(rest.resource, params, {
        item: items[oldIndex],
        weight: items[newIndex]
          ? items[newIndex].weight
          : _last(items).weight + 1,
      });

      notify('Collection sorted', 'info', {}, false);
    }
  }

  if (!items) {
    return null;
  }

  return (
    <>
      {progress !== null && <Overlay progress={progress} />}
      <ImageList
        sx={{
          width: 'calc(100% - 40px)',
          margin: 0,
          padding: '20px',
          gridTemplateColumns:
            'repeat(auto-fill, minmax(280px, 1fr)) !important',
        }}
        cols={3}
        gap={20}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items}>
            {items.map((collection) => {
              const url = `${_get(
                collection,
                'imageUrl'
              )}?width=500&format=jpg`;
              return (
                <SortableItem key={collection.id} id={collection.id}>
                  <Badge badgeContent={collection.weight} color="primary">
                    <ImageListItem
                      key={url}
                      sx={{
                        opacity: collection.status === 'draft' ? 0.6 : 1,
                      }}
                    >
                      <img
                        src={url}
                        alt={collection.title}
                        loading="lazy"
                        style={{
                          height: '180px',
                        }}
                      />
                      <ImageListItemBar
                        title={collection.title}
                        subtitle={`Status : ${collection.status}`}
                      />
                    </ImageListItem>
                  </Badge>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </ImageList>
    </>
  );
};

export default CollectionGrid;
